import { useQuery } from '@tanstack/react-query'
import { NO_REQUEST_RECORD_PERMISSIONS, type RequestRecordPermissions } from '@omnea-app/authz'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'

export const GetRequestRecordPermissionsQueryKey = 'GetRequestRecordPermissions'

const fiveMinutesMs = 5 * 60 * 1000

/**
 * `legacy` means the organisation is not on the permissions service, so the caller must keep using
 * whatever access rule it used before rather than treating an absent answer as a denial.
 *
 * `unavailable` carries an all-false map so affordances withhold, but stays distinct from `resolved`
 * because a check that did not answer is not a denial.
 */
export type RequestRecordPermissionsState =
  | { status: 'loading' }
  | { status: 'legacy' }
  | { status: 'unavailable'; permissions: RequestRecordPermissions; error: unknown }
  | { status: 'resolved'; permissions: RequestRecordPermissions }

type PermissionsResponse = { kind: 'permissions'; permissions: RequestRecordPermissions } | { kind: 'legacy' }

/**
 * What the current user may do on one request, per the permissions service. A denial is a populated
 * all-false map, not an error, so a caller renders from this alone; a fetch failure fails closed the
 * same way. Mirrors useSupplierRecordPermissions.
 */
export const useRequestRecordPermissions = (requestId: string, options: { enabled: boolean }): RequestRecordPermissionsState => {
  const { getToken, user } = useAuthenticatedUser()
  const { data, isError, error } = useQuery({
    queryKey: [GetRequestRecordPermissionsQueryKey, user.id, requestId],
    queryFn: async (): Promise<PermissionsResponse> => {
      const authorization = await getToken()
      const permissionsClient = new PermissionsHttpClient({ authorization })
      const { data: response, error } = await permissionsClient.GET('/entities/{entity}/{recordId}/permissions', {
        params: { path: { entity: 'request', recordId: requestId } },
      })
      // The typed error body identifies the 404; a request that failed before a response existed has no status at all.
      if (error != null && 'type' in error && error.type === 'NotFoundError') return { kind: 'legacy' }
      throwIfApiError(error)
      const { recordPermissions } = response
      if (recordPermissions.entity !== 'request') throw new Error('request permissions request answered for another entity')
      return { kind: 'permissions', permissions: recordPermissions.permissions }
    },
    enabled: options.enabled,
    // Access changes when a grant is edited, not on the seconds-long default the shared client assumes.
    staleTime: fiveMinutesMs,
  })

  if (!options.enabled) return { status: 'legacy' }
  // Read before isError so a failed background refetch keeps the last-known answer rather than
  // collapsing every affordance mid-session.
  if (data != null) {
    return data.kind === 'legacy' ? { status: 'legacy' } : { status: 'resolved', permissions: data.permissions }
  }
  if (isError) return { status: 'unavailable', permissions: NO_REQUEST_RECORD_PERMISSIONS, error }
  return { status: 'loading' }
}
